import { BISHOP, type Color, FLAG_CAPTURE, QUEEN, ROOK, TYPE_MASK, colorBit, encodeMove, moveFrom, moveTo, other } from '../board.ts';
import { DIAGONAL, ORTHOGONAL } from '../pieces.ts';
import type { MoveContext } from '../rules.ts';
import type { Position, Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 12 (draft). There are portals on c4 and f5. A rook, bishop or queen whose path crosses
// an empty portal may come out of the other one and keep going in the same direction (the
// far portal is the first square after the jump). One jump per move. Portals are ordinary
// squares otherwise: pieces can stand on them, and a piece standing on a portal blocks it.
// Checks through the portals count (the `attacks` hook mirrors `generate`).

export const PORTALS = [26, 37] as const; // c4, f5
const twin = (sq: number) => (sq === PORTALS[0] ? PORTALS[1] : sq === PORTALS[1] ? PORTALS[0] : -1);

const DIRS: [number, number][] = [...ORTHOGONAL, ...DIAGONAL].map(([df, dr]) => [df, dr]);
const step = (sq: number, df: number, dr: number) => {
  const f = (sq & 7) + df;
  const r = (sq >> 3) + dr;
  return f < 0 || f > 7 || r < 0 || r > 7 ? -1 : r * 8 + f;
};
const slides = (type: number, diagonal: boolean) => type === QUEEN || type === (diagonal ? BISHOP : ROOK);

const seen = new Int32Array(64);
let pass = 0;

/** Moves through the portals for the side to move (straight moves come from the engine). */
function portalMoves(pos: Position, out: number[], capturesOnly: boolean) {
  const b = pos.board;
  const usBit = colorBit(pos.turn);
  const themBit = colorBit(other(pos.turn));
  for (let sq = 0; sq < 64; sq++) {
    const p = b[sq];
    if (!(p & usBit)) continue;
    const type = p & TYPE_MASK;
    if (type !== ROOK && type !== BISHOP && type !== QUEEN) continue;
    pass++;
    seen[sq] = pass;
    // Straight targets first, so a square reachable both ways is only generated once.
    for (const [df, dr] of DIRS) {
      if (!slides(type, df !== 0 && dr !== 0)) continue;
      for (let cur = step(sq, df, dr); cur >= 0; cur = step(cur, df, dr)) {
        seen[cur] = pass;
        if (b[cur] !== 0) break;
      }
    }
    for (const [df, dr] of DIRS) {
      if (!slides(type, df !== 0 && dr !== 0)) continue;
      for (let cur = step(sq, df, dr); cur >= 0 && b[cur] === 0; cur = step(cur, df, dr)) {
        const exit = twin(cur);
        if (exit < 0) continue;
        // Through the portal: the exit square, then onwards.
        for (let t = exit; t >= 0; t = step(t, df, dr)) {
          const q = b[t];
          if (q & usBit) break;
          if (seen[t] !== pass) {
            seen[t] = pass;
            if (q) out.push(encodeMove(sq, t, 0, FLAG_CAPTURE));
            else if (!capturesOnly) out.push(encodeMove(sq, t));
          }
          if (q) break;
        }
        break;
      }
    }
  }
}

/** Whether a slider of `by` attacks `sq` through a portal. */
export function portalAttack(board: Int8Array, sq: number, by: Color): boolean {
  const bit = colorBit(by);
  for (const [df, dr] of DIRS) {
    const diagonal = df !== 0 && dr !== 0;
    // Walk back from the target; at each portal on the way (the target itself included),
    // jump to its twin, which must be empty, and look for a slider behind it.
    for (let cur = sq; cur >= 0; ) {
      const entry = twin(cur);
      if (entry >= 0 && board[entry] === 0) {
        for (let s = step(entry, -df, -dr); s >= 0; s = step(s, -df, -dr)) {
          const q = board[s];
          if (q === 0) continue;
          if (q & bit && slides(q & TYPE_MASK, diagonal)) return true;
          break;
        }
      }
      cur = step(cur, -df, -dr);
      if (cur >= 0 && board[cur] !== 0) break;
    }
  }
  return false;
}

export const portal: Variant = {
  id: 'portal',
  info: {
    name: 'Portal Chess',
    but: 'there are portals',
    tagline: 'Slide into one portal, come out of the other. Checkmate from nowhere.',
    summary: 'Two portals, on c4 and f5. A rook, bishop or queen that slides into one can **come out of the other** and keep going.',
    rules: [
      'There are portals on c4 and f5.',
      'A rook, bishop or queen whose path crosses an empty portal can come out of the other portal and keep moving the same way. One jump per move.',
      'Pieces can stand on portals; a piece on either portal blocks the jump. Knights, kings and pawns ignore portals.',
      'Checks through a portal count.',
      'Otherwise normal chess: checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#4cc9f0',
    emoji: '🌀',
    bots: ['Blink', 'Wormhole', 'Warp Drive', 'Event Horizon', 'Spacetime'],
  },
  startFen: STANDARD_FEN,
  // c4 and f5 are the same colour, so bishops stay on theirs and dead positions stay dead.
  insufficientMaterial: true,

  generate(pos, out, capturesOnly) {
    portalMoves(pos, out, capturesOnly);
  },

  attacks: portalAttack,

  afterMove(ctx: MoveContext) {
    const type = ctx.piece & TYPE_MASK;
    if (ctx.events && (type === ROOK || type === BISHOP || type === QUEEN) && throughPortal(ctx.prev.board, type, ctx.move)) {
      ctx.fx('portal', moveTo(ctx.move));
    }
  },
};

/** Whether a slider's move is one it couldn't make in a straight line (for the UI). */
export function throughPortal(board: Int8Array, type: number, move: number): boolean {
  const from = moveFrom(move);
  const to = moveTo(move);
  const fd = (to & 7) - (from & 7);
  const rd = (to >> 3) - (from >> 3);
  const straight = fd === 0 || rd === 0;
  const diagonal = Math.abs(fd) === Math.abs(rd);
  if (!(straight && type !== BISHOP) && !(diagonal && type !== ROOK)) return true;
  // On one of its lines: straight unless something stands in between.
  const df = Math.sign(fd);
  const dr = Math.sign(rd);
  for (let cur = step(from, df, dr); cur >= 0; cur = step(cur, df, dr)) {
    if (cur === to) return false;
    if (board[cur] !== 0) return true;
  }
  return true;
}
